class ConceptModalController {
  constructor() {
    this.modal = document.getElementById('concept-modal')
    if (!this.modal) return
    
    this.currentConcept = null
    this.allConcepts = []
    this.currentIndex = -1
    this.setupModal()
    this.loadConcepts()
    this.bindEvents()
    window.ConceptModalControllerInstance = this
  }

  setupModal() {
    this.image = this.modal.querySelector('#modal-image')
    this.title = this.modal.querySelector('.concept-title h2')
    this.code = this.modal.querySelector('.concept-title .code')
    this.versionCount = this.modal.querySelector('.version-count span')
    this.sourceInfo = this.modal.querySelector('.source-info span')
    this.finalVersion = this.modal.querySelector('.final-version')
    this.detailsLink = this.modal.querySelector('.details-link')
    this.prevButton = this.modal.querySelector('.nav-button.prev')
    this.nextButton = this.modal.querySelector('.nav-button.next')
    this.upButton = this.modal.querySelector('.nav-button.up')
    this.downButton = this.modal.querySelector('.nav-button.down')
    this.indexNav = this.modal.querySelector('.version-index-nav')
    this.modalRating = this.modal.querySelector('#modal-rating')
    this.commentsList = this.modal.querySelector('.comments-list')
    this.commentForm = this.modal.querySelector('.comment-form')
    this.hideButton = this.modal.querySelector('.hide-version')
    
    this.setupCommentForm()
  }

  loadConcepts() {
    this.allConcepts = Array.from(document.querySelectorAll('.concept-card')).map(card => {
      return JSON.parse(card.dataset.concept)
    })
  }

  bindEvents() {
    // Bind per-card events (idempotent)
    this.bindCardEvents(document)

    // Bind one-time global events
    if (!this._globalsBound) {
      this._globalsBound = true
      this.prevButton.addEventListener('click', (e) => { e.preventDefault(); this.showPrevious() })
      this.nextButton.addEventListener('click', (e) => { e.preventDefault(); this.showNext() })
      if (this.upButton) this.upButton.addEventListener('click', (e) => { e.preventDefault(); this.showPrevVersion() })
      if (this.downButton) this.downButton.addEventListener('click', (e) => { e.preventDefault(); this.showNextVersion() })

      document.addEventListener('keydown', (e) => {
        if (!this.modal.style.display || this.modal.style.display === 'none') return
        switch(e.key) {
          case 'ArrowLeft': e.preventDefault(); this.showPrevious(); break
          case 'ArrowRight': e.preventDefault(); this.showNext(); break
          case 'ArrowUp': e.preventDefault(); this.showPrevVersion(); break
          case 'ArrowDown': e.preventDefault(); this.showNextVersion(); break
          case 'Escape': e.preventDefault(); this.hideModal(); break
        }
      })

      this.modal.querySelector('.modal-close').addEventListener('click', () => { this.hideModal() })
      window.addEventListener('click', (e) => { if (e.target === this.modal) this.hideModal() })

      // Keep modal state (stars) in sync when rating happens
      document.addEventListener('version-rating-updated', (e) => {
        try {
          const detail = e.detail || {}
          if (!this.currentConcept || Number(this.currentConcept.id) !== Number(detail.conceptId)) return
          const versions = this.currentConcept.versions || []
          const idx = versions.findIndex(v => Number(v.id) === Number(detail.versionId))
          if (idx >= 0) {
            versions[idx].user_score = Number(detail.user_rating || 0)
            // If the currently shown version was updated, refresh stars immediately
            if (Number(this.currentConcept.current_version_index || 0) === idx) {
              this.renderStars(versions[idx].user_score)
            }
          }
        } catch (_) {}
      })

      // Mark current version as final
      const finalBtn = this.modal.querySelector('.mark-final')
      if (finalBtn) {
        finalBtn.addEventListener('click', async (e) => {
          e.preventDefault()
          const versions = this.currentConcept?.versions || []
          const idx = this.currentConcept?.current_version_index || 0
          const versionId = versions[idx]?.id
          const conceptId = this.currentConcept?.id
          if (!versionId || !conceptId) return
          try {
            const res = await fetch(`/admin/artwork_concepts/${conceptId}/artwork_versions/${versionId}/select_as_final.json`, {
              method: 'PATCH',
              headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
              }
            })
            if (!res.ok) throw new Error('Final select failed')
            const data = await res.json()
            this.currentConcept.selected_version_id = data.selected_version_id
            this.renderIndexNav()
          } catch(err) {
            console.error(err)
          }
        })
      }

      // Hide current version
      if (this.hideButton) {
        this.hideButton.addEventListener('click', async (e) => {
          e.preventDefault()
          const versions = this.currentConcept?.versions || []
          const idx = this.currentConcept?.current_version_index || 0
          const versionId = versions[idx]?.id
          const conceptId = this.currentConcept?.id
          if (!versionId || !conceptId) return
          if (!confirm('Deze versie verbergen?')) return
          try {
            const res = await fetch(`/admin/artwork_concepts/${conceptId}/artwork_versions/${versionId}/hide.json`, {
              method: 'PATCH',
              headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
              }
            })
            if (!res.ok) throw new Error('Hide failed')
            const data = await res.json()
            // Remove hidden version from local payload
            versions.splice(idx, 1)
            this.currentConcept.versions = versions
            // If selected version was this one, sync selected_version_id
            if (typeof data.selected_version_id !== 'undefined') {
              this.currentConcept.selected_version_id = data.selected_version_id
            }
            // Adjust current index
            const newIdx = Math.min(idx, Math.max(versions.length - 1, 0))
            this.currentConcept.current_version_index = newIdx
            // Update counts and UI
            this.versionCount.textContent = String(versions.length)
            if (versions.length === 0) {
              this.image.src = ''
              this.indexNav.innerHTML = ''
              this.showPlaceholderComments()
            } else {
              this.swapVersionImage()
              this.renderIndexNav()
              this.updateNavigationButtons()
              if (this.modalRating) {
                const v = versions[newIdx]
                this.modalRating.dataset.versionId = String(v?.id || '')
                this.updateRatingStarsForCurrentVersion()
              }
            }
          } catch(err) {
            console.error(err)
          }
        })
      }
    }
  }

  bindCardEvents(root) {
    const container = root || document
    container.querySelectorAll('.concept-card').forEach(card => {
      if (card.dataset.modalBound === '1') return
      card.dataset.modalBound = '1'

      card.addEventListener('click', (e) => {
        const inActions = e.target.closest('.concept-actions')
        if (inActions) {
          // allow default (e.g., Rails UJS delete), but do not bubble to card
          e.stopPropagation()
          return
        }
        // Otherwise, allow default link navigation on the card
      })
    })
  }

  setupCommentForm() {
    this.commentForm.addEventListener('submit', (e) => {
      e.preventDefault()
      const textarea = this.commentForm.querySelector('textarea')
      const comment = textarea.value.trim()
      if (comment) {
        this.submitComment(comment, { reload: true })
        textarea.value = ''
      }
    })

    const textarea = this.commentForm.querySelector('textarea')
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        const comment = textarea.value.trim()
        if (comment) {
          this.submitComment(comment, { reload: true })
          textarea.value = ''
        }
      }
    })
  }

  submitComment(body, opts = {}) {
    const versions = this.currentConcept?.versions || []
    const idx = this.currentConcept?.current_version_index || 0
    const versionId = (versions[idx] && versions[idx].id) || this.currentConcept.selected_version_id
    if (!versionId) return
    console.log('Submitting comment:', { body, versionId })
    
    fetch(`/admin/artwork_concepts/${this.currentConcept.id}/artwork_versions/${versionId}/version_comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ 
        version_comment: { body }
      })
    })
    .then(async (response) => {
      if (!response.ok) {
        const ct = response.headers.get('content-type') || ''
        if (ct.includes('application/json')) {
          const data = await response.json()
          console.error('Error saving comment (json):', data)
          throw new Error(data.errors?.join(', ') || 'Failed to save comment')
        } else {
          const text = await response.text()
          console.error('Error saving comment (text):', text)
          throw new Error('Failed to save comment')
        }
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        console.log('Comment saved successfully:', data)
        if (opts.reload) {
          this.loadComments(versionId)
        } else {
          this.addCommentToList({
            id: data.comment.id,
            body: data.comment.body,
            author: data.comment.author,
            created_at: data.comment.created_at,
            can_edit: true,
            version_id: data.comment.version_id
          })
        }
      }
    })
    .catch(error => {
      console.error('Error:', error)
      // Hier kun je een foutmelding tonen aan de gebruiker
    })
  }

  loadComments(versionId) {
    fetch(`/admin/artwork_concepts/${this.currentConcept.id}/artwork_versions/${versionId}/version_comments`, {
      headers: { 'Accept': 'application/json' }
    })
    .then(res => res.json())
    .then(list => {
      // Replace without any fade/transitions
      this.commentsList.style.transition = 'none'
      this.commentsList.innerHTML = ''
      if (!list || list.length === 0) {
        this.commentsList.innerHTML = '<div class="no-comments">Nog geen opmerkingen</div>'
        return
      }
      list.forEach(c => this.addCommentToList(c))
    })
    .catch(err => console.error('Load comments error:', err))
  }

  showModalForConcept(concept) {
    this.currentIndex = this.allConcepts.findIndex(c => c.id === concept.id)
    this.updateNavigationButtons()
    this.showModal(concept)
  }

  showPrevious() {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.showModal(this.allConcepts[this.currentIndex])
      this.updateNavigationButtons()
    }
  }

  showNext() {
    if (this.currentIndex < this.allConcepts.length - 1) {
      this.currentIndex++
      this.showModal(this.allConcepts[this.currentIndex])
      this.updateNavigationButtons()
    }
  }

  updateNavigationButtons() {
    this.prevButton.style.display = this.currentIndex > 0 ? 'flex' : 'none'
    this.nextButton.style.display = this.currentIndex < this.allConcepts.length - 1 ? 'flex' : 'none'
    const versions = (this.currentConcept && this.currentConcept.versions) || []
    const idx = this.currentConcept?.current_version_index || 0
    if (this.upButton && this.downButton) {
      this.upButton.style.opacity = versions.length > 1 && idx > 0 ? '1' : '0.4'
      this.downButton.style.opacity = versions.length > 1 && idx < versions.length - 1 ? '1' : '0.4'
    }
  }

  showModal(concept) {
    this.currentConcept = concept
    
    const versions = concept.versions || []
    const idx = Number.isInteger(concept.current_version_index) ? concept.current_version_index : 0
    if (versions[idx]) {
      this.image.src = versions[idx].image_url
    } else {
      this.image.src = concept.image_url || ''
    }
    // no animated transition

    this.title.textContent = concept.title
    this.code.textContent = concept.code
    this.versionCount.textContent = concept.version_count
    this.sourceInfo.textContent = concept.source || 'Geen bron'
    this.finalVersion.style.display = concept.has_final_version ? 'flex' : 'none'
    this.detailsLink.href = `/admin/artwork_concepts/${concept.id}`
    // Bind rating container to current ids
    if (this.modalRating) {
      this.modalRating.dataset.conceptId = String(concept.id)
      this.modalRating.dataset.versionId = String(versions[idx]?.id || '')
      const userScore = Number(versions[idx]?.user_score || 0)
      this.renderStars(userScore)
    }
    
    // Build/refresh index navigation (1..N)
    this.renderIndexNav()

    // Load comments for the current variant
    this.loadComments(versions[idx]?.id || concept.selected_version_id)
    
    this.modal.style.display = 'flex'
    document.body.style.overflow = 'hidden'
    if (versions.length > 1) this.flashNavVisible()
    this.onAfterOpenTick()
  }

  hideModal() {
    this.modal.style.display = 'none'
    document.body.style.overflow = ''
    this.currentConcept = null
    this.currentIndex = -1
  }

  renderIndexNav() {
    if (!this.indexNav) return
    const versions = this.currentConcept?.versions || []
    const idx = this.currentConcept?.current_version_index || 0
    this.indexNav.innerHTML = ''
    versions.forEach((v, i) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'index-button' + (i === idx ? ' active' : '') + (Number(this.currentConcept.selected_version_id) === Number(v.id) ? ' final' : '')
      btn.textContent = String(i + 1)
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        if (this.currentConcept.current_version_index !== i) {
          this.currentConcept.current_version_index = i
          this.swapVersionImage()
          this.updateNavigationButtons()
          this.renderIndexNav()
          this.loadComments(v.id)
          if (this.modalRating) {
            this.modalRating.dataset.versionId = String(v.id)
            this.updateRatingStarsForCurrentVersion()
          }
        }
      })
      this.indexNav.appendChild(btn)
    })
  }

  addCommentToList(comment) {
    const commentHtml = `
      <div class="comment" data-comment-id="${comment.id}">
        <div class="comment-header">
          <span class="author">${comment.author}</span>
          <span class="date">${comment.created_at}</span>
          ${comment.can_edit ? `
            <div class="comment-actions">
              <button class="action-link edit" title="Bewerk">
                <i class="fas fa-edit"></i>
              </button>
              <button class="action-link delete" title="Verwijder">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          ` : ''}
        </div>
        <div class="comment-body">${comment.body}</div>
      </div>
    `
    
    if (this.commentsList.querySelector('.no-comments')) {
      this.commentsList.innerHTML = ''
    }
    this.commentsList.insertAdjacentHTML('beforeend', commentHtml)

    if (comment.can_edit) {
      const node = this.commentsList.querySelector(`.comment[data-comment-id="${comment.id}"]`)
      const editBtn = node.querySelector('.action-link.edit')
      const delBtn = node.querySelector('.action-link.delete')
      editBtn.addEventListener('click', (e) => {
        e.preventDefault()
        this.inlineEditComment(node, comment)
      })
      delBtn.addEventListener('click', (e) => {
        e.preventDefault()
        this.deleteComment(node, comment)
      })
    }
  }

  inlineEditComment(node, comment) {
    const bodyEl = node.querySelector('.comment-body')
    const original = bodyEl.textContent
    bodyEl.innerHTML = `
      <textarea class="inline-edit">${original}</textarea>
      <div class="inline-edit-actions">
        <button class="button small save">Opslaan</button>
        <button class="button small cancel">Annuleren</button>
      </div>
    `
    const textarea = bodyEl.querySelector('textarea.inline-edit')
    bodyEl.querySelector('.save').addEventListener('click', () => {
      const versions = this.currentConcept?.versions || []
      const idx = this.currentConcept?.current_version_index || 0
      const versionId = (versions[idx] && versions[idx].id) || this.currentConcept.selected_version_id
      if (!versionId) return
      fetch(`/admin/artwork_concepts/${this.currentConcept.id}/artwork_versions/${versionId}/version_comments/${comment.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ version_comment: { body: textarea.value } })
      }).then(async (res) => {
        if (!res.ok) {
          const text = await res.text()
          throw new Error(text)
        }
        bodyEl.textContent = textarea.value
      }).catch(err => console.error('Update comment error:', err))
    })
    bodyEl.querySelector('.cancel').addEventListener('click', () => {
      bodyEl.textContent = original
    })
  }

  deleteComment(node, comment) {
    if (!confirm('Weet je zeker dat je deze opmerking wilt verwijderen?')) return
    const versions = this.currentConcept?.versions || []
    const idx = this.currentConcept?.current_version_index || 0
    const versionId = (versions[idx] && versions[idx].id) || this.currentConcept.selected_version_id
    if (!versionId) return
    fetch(`/admin/artwork_concepts/${this.currentConcept.id}/artwork_versions/${versionId}/version_comments/${comment.id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    }).then(res => {
      if (!res.ok) throw new Error('Delete failed')
      node.remove()
      if (!this.commentsList.querySelector('.comment')) {
        this.commentsList.innerHTML = '<div class="no-comments">Nog geen opmerkingen</div>'
      }
    }).catch(err => console.error('Delete comment error:', err))
  }

  showPrevVersion() {
    const versions = this.currentConcept?.versions || []
    if (versions.length === 0) return
    const idx = this.currentConcept.current_version_index || 0
    if (idx > 0) {
      this.currentConcept.current_version_index = idx - 1
      this.swapVersionImage()
      this.updateNavigationButtons()
      this.renderIndexNav()
      this.flashNavVisible()
      if (this.modalRating) {
        const v = versions[this.currentConcept.current_version_index]
        this.modalRating.dataset.versionId = String(v?.id || '')
        this.updateRatingStarsForCurrentVersion()
      }
    }
  }

  showNextVersion() {
    const versions = this.currentConcept?.versions || []
    if (versions.length === 0) return
    const idx = this.currentConcept.current_version_index || 0
    if (idx < versions.length - 1) {
      this.currentConcept.current_version_index = idx + 1
      this.swapVersionImage()
      this.updateNavigationButtons()
      this.renderIndexNav()
      this.flashNavVisible()
      if (this.modalRating) {
        const v = versions[this.currentConcept.current_version_index]
        this.modalRating.dataset.versionId = String(v?.id || '')
        this.updateRatingStarsForCurrentVersion()
      }
    }
  }

  swapVersionImage() {
    const versions = this.currentConcept?.versions || []
    const idx = this.currentConcept?.current_version_index || 0
    const nextUrl = versions[idx]?.image_url
    if (!nextUrl) return
    const img = new Image()
    img.onload = () => {
      this.image.src = nextUrl
      // no animated transition
      this.showPlaceholderComments()
      // Load comments for the newly selected variant
      const versionId = versions[idx]?.id || this.currentConcept.selected_version_id
      if (versionId) this.loadComments(versionId)
      if (this.modalRating) {
        this.modalRating.dataset.versionId = String(versionId || '')
        // Render stars directly from version payload to avoid stale DOM state
        const vIdx = versions.findIndex(v => Number(v.id) === Number(versionId))
        const userScore = Number((vIdx >= 0 ? versions[vIdx]?.user_score : 0) || 0)
        this.renderStars(userScore)
      }
    }
    img.src = nextUrl
  }

  updateRatingStarsForCurrentVersion() {
    if (!this.modalRating) return
    const versions = this.currentConcept?.versions || []
    const versionId = Number(this.modalRating.dataset.versionId || 0)
    const idx = versions.findIndex(v => Number(v.id) === versionId)
    const userScore = Number((idx >= 0 ? versions[idx]?.user_score : 0) || 0)
    this.renderStars(userScore)
  }

  // Ensure stars reflect the current version also on initial modal open after DOM paint
  onAfterOpenTick() {
    setTimeout(() => this.updateRatingStarsForCurrentVersion(), 0)
  }

  renderStars(userScore) {
    const starsWrap = this.modalRating?.querySelector('.stars')
    if (!starsWrap) return
    const score = Number(userScore || 0)
    let html = ''
    for (let i = 1; i <= 5; i++) {
      const filled = i <= score
      html += `<i class="${filled ? 'fas' : 'far'} fa-star" data-score="${i}" data-action="click->rating#rate"></i>`
    }
    starsWrap.innerHTML = html
    // Update average if available in payload
    try {
      const versions = this.currentConcept?.versions || []
      const versionId = Number(this.modalRating?.dataset?.versionId || 0)
      const idx = versions.findIndex(v => Number(v.id) === versionId)
      const avg = (idx >= 0 ? Number(versions[idx]?.average) : NaN)
      const avgNode = this.modalRating?.querySelector('.average-rating .score')
      if (avgNode) {
        avgNode.textContent = isNaN(avg) ? '' : String(Math.round(avg * 10) / 10)
      }
    } catch(_) {}
  }

  flashNavVisible() {
    const nav = this.modal.querySelector('.image-navigation')
    if (!nav) return
    nav.classList.add('nav-visible')
    clearTimeout(this._navTimer)
    this._navTimer = setTimeout(() => {
      nav.classList.remove('nav-visible')
    }, 1200)
  }

  showPlaceholderComments() {
    if (!this.commentsList) return
    this.commentsList.innerHTML = '<div class="no-comments">Nog geen opmerkingen</div>'
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ConceptModalController()
}) 